/**
 * Read-only holdings table grouped by account wrapper, with per-group
 * subtotals and a grand total — replaces the old always-editable flat table
 * (#1533). Editing happens in HoldingEditDrawer, opened on row click.
 */

import { Fragment } from 'react';
import { HoldingData } from '../../types/wizard';
import { formatNumber } from '../../utils/formatters';
import { formatOcf } from './holdingsFormat';

const WRAPPER_ORDER: ReadonlyArray<{ type: HoldingData['accountType']; label: string }> = [
  { type: 'SIPP', label: 'SIPP' },
  { type: 'ISA', label: 'ISA' },
  { type: 'BROKERAGE', label: 'Brokerage' },
];

const CLASS_LABELS: Record<HoldingData['assetClass'], string> = {
  EQUITY: 'Equity',
  BOND: 'Bond',
  MIXED: 'Mixed',
  OTHER: 'Other',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Bonds don't go through unit-price tracking (no live price feed for gilts),
 * but an individual bond's nominal value IS its quantity — already captured
 * via the maturity/coupon details form.
 */
function getBondNominal(holding: HoldingData): number | undefined {
  return holding.assetClass === 'BOND' ? holding.bondDetails?.nominalValue : undefined;
}

/** Clean price per £100 nominal — the standard gilt quoting convention. */
function getBondImpliedPrice(holding: HoldingData, bondNominal: number | undefined): number | undefined {
  if (!bondNominal || bondNominal <= 0) return undefined;
  return ((holding.currentValue || 0) / bondNominal) * 100;
}

function getQtyDisplay(holding: HoldingData, isTracked: boolean | undefined, bondNominal: number | undefined): string {
  if (isTracked) return formatNumber(holding.quantity!, 2);
  if (bondNominal != null) return formatNumber(bondNominal, 0);
  return '—';
}

function getPriceDisplay(holding: HoldingData, isTracked: boolean | undefined, bondImpliedPrice: number | undefined): string {
  if (isTracked) return `£${formatNumber(holding.unitPrice!, 2)}`;
  if (bondImpliedPrice != null) return `£${formatNumber(bondImpliedPrice, 2)}`;
  return '—';
}

interface HoldingsTableProps {
  holdings: ReadonlyArray<HoldingData>;
  onRowClick: (holdingIndex: number) => void;
  onAddInvestment: () => void;
  onFetchAllPrices: () => void;
  fetchingPrices: boolean;
  fetchablePriceCount: number;
  lastFetchedLabel: string | null;
}

function HoldingsTable({
  holdings,
  onRowClick,
  onAddInvestment,
  onFetchAllPrices,
  fetchingPrices,
  fetchablePriceCount,
  lastFetchedLabel,
}: Readonly<HoldingsTableProps>) {
  const groups = WRAPPER_ORDER.map(({ type, label }) => ({
    type,
    label,
    items: holdings
      .map((holding, originalIndex) => ({ holding, originalIndex }))
      .filter(({ holding }) => holding.accountType === type),
  })).filter(group => group.items.length > 0);

  const grandTotal = holdings.reduce((sum, h) => sum + (h.currentValue || 0), 0);

  return (
    <div className="holdings-table-wrapper">
      <div className="holdings-toolbar">
        <span className="holdings-toolbar-meta">
          {lastFetchedLabel || `${holdings.length} holding${holdings.length === 1 ? '' : 's'}`}
          <span className="holdings-toolbar-hint"> · Click a holding to edit</span>
        </span>
        <div className="holdings-toolbar-actions">
          {fetchablePriceCount > 0 && (
            <button
              type="button"
              className="holdings-fetch-btn"
              disabled={fetchingPrices}
              onClick={onFetchAllPrices}
            >
              <span className={`holdings-fetch-icon ${fetchingPrices ? 'fetching' : ''}`} aria-hidden="true">&#8635;</span>
              {fetchingPrices ? 'Fetching…' : 'Fetch prices'}
            </button>
          )}
          <button type="button" className="add-holding-btn add-holding-btn-toolbar" onClick={onAddInvestment}>
            + Add Investment
          </button>
        </div>
      </div>

      <table className="holdings-table">
        <thead>
          <tr>
            <th className="col-fund-name">Symbol / Name</th>
            <th className="col-class">Class</th>
            <th className="col-ocf">Fee</th>
            <th className="col-qty">Qty</th>
            <th className="col-price">Price</th>
            <th className="col-value">Value (&#163;)</th>
            <th className="col-chevron" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const groupTotal = group.items.reduce((sum, { holding }) => sum + (holding.currentValue || 0), 0);
            return (
              <Fragment key={group.type}>
                <tr className="holdings-group-row">
                  <td colSpan={7}>{group.label}</td>
                </tr>
                {group.items.map(({ holding, originalIndex }) => {
                  const isTracked = holding.quantityTracked && holding.quantity != null && holding.unitPrice != null;
                  const bondNominal = getBondNominal(holding);
                  const bondImpliedPrice = getBondImpliedPrice(holding, bondNominal);
                  const qtyDisplay = getQtyDisplay(holding, isTracked, bondNominal);
                  const priceDisplay = getPriceDisplay(holding, isTracked, bondImpliedPrice);
                  return (
                    <tr
                      key={holding.holdingId}
                      className="holdings-row holdings-row-readonly"
                      tabIndex={0}
                      role="button"
                      aria-label={`Edit ${holding.etfName || 'holding'}`}
                      onClick={() => onRowClick(originalIndex)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(originalIndex);
                        }
                      }}
                    >
                      <td className="col-fund-name">
                        {holding.ticker && <span className="holding-ticker">{holding.ticker}</span>}
                        <span className="holding-name">{holding.etfName || 'Untitled holding'}</span>
                      </td>
                      <td className="col-class">
                        <span className={`class-pill class-pill-${holding.assetClass.toLowerCase()}`}>
                          {CLASS_LABELS[holding.assetClass]}
                        </span>
                      </td>
                      <td className="col-ocf">{holding.ocfPct ? formatOcf(holding.ocfPct) : '—'}</td>
                      <td className="col-qty tabular-nums">{qtyDisplay}</td>
                      <td className="col-price tabular-nums" title={bondImpliedPrice != null ? 'Implied clean price per £100 nominal' : undefined}>{priceDisplay}</td>
                      <td className="col-value tabular-nums">{formatCurrency(holding.currentValue || 0)}</td>
                      <td className="col-chevron" aria-hidden="true"><span className="row-chevron">&#8250;</span></td>
                    </tr>
                  );
                })}
                <tr className="holdings-subtotal-row">
                  <td>{group.label} Total</td>
                  <td /><td /><td /><td />
                  <td className="col-value tabular-nums">{formatCurrency(groupTotal)}</td>
                  <td />
                </tr>
              </Fragment>
            );
          })}
          <tr className="holdings-total-row">
            <td>Total</td>
            <td /><td /><td /><td />
            <td className="col-value tabular-nums">{formatCurrency(grandTotal)}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default HoldingsTable;
